// src/components/Sitzungen.jsx
// Tab "Sitzungen" der Projekt-Detailansicht: Sitzungsgefässe (Jour fix etc.)
// erfassen, bearbeiten, löschen. Anwesende stammen aus denselben Quellen wie die
// Verantwortlichen bei Pendenzen (Malheur&Fortuna/Organisation/Person/Team) –
// bewusste Abweichung von der ursprünglichen "projectPersons"-Formulierung,
// analog ADR 0009/0012 (siehe docs/adr zu US-SITZ-001).
// US-SITZ-001: Sitzungsgefäss erfassen/bearbeiten/löschen, Anwesende mit Funktion.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getSitzungen,
  createSitzung,
  updateSitzung,
  deleteSitzung,
  suggestNextNr,
  SITZUNG_ARTEN,
} from "../services/azureSitzungenService";
import { getAllPartners } from "../services/azurePartnersService";
import { getAllPersons } from "../services/azurePersonsService";
import { getProjectRelations } from "../services/azureRelationsService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { getPendenzen } from "../services/azurePendenzenService";
import { buildResponsibleOptions } from "./Pendenzen.jsx";
import {
  downloadProtokoll,
  buildProtokollRecipients,
  buildProtokollMailto,
  protokollFilename,
} from "../utils/generateProtokoll";
import { downloadSitzungPdf, sitzungPdfFilename } from "../utils/generateSitzungPdf";
import { getPraesentation } from "../services/azureSitzungFolienService";
import { useAuth } from "../hooks/useAuth";
import "../styles/sitzungen.scss";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const emptyForm = () => ({
  art: SITZUNG_ARTEN[0],
  nr: "",
  datum: "",
  ort: "",
  anwesende: [],
});

// --- Mehrfachauswahl der Anwesenden mit Funktion je Person -------------------
// Exportiert, damit der Sitzungsmodus in Pendenzen.jsx dieselbe Erfassung nutzt.
export const AttendeeSelect = ({ options, attendees, onChange }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedIds = attendees.map((a) => a.optionId);
  const q = query.trim();
  const available = options
    .filter((o) => !selectedIds.includes(o.id))
    .filter((o) => (q ? o.label.toLowerCase().includes(q.toLowerCase()) : true));
  // "neue hinzufügen": freie Person (nicht im CRM/Team), nur für die Anwesenheit.
  const canAddFree =
    !!q &&
    !options.some((o) => o.label.toLowerCase() === q.toLowerCase()) &&
    !attendees.some((a) => (a.label || "").toLowerCase() === q.toLowerCase());

  const add = (option) => {
    onChange([...attendees, { optionId: option.id, label: option.label, funktion: "" }]);
    setQuery("");
  };
  const addFree = (name) => {
    onChange([...attendees, { optionId: `free:${name}`, label: name, funktion: "" }]);
    setQuery("");
  };
  const remove = (optionId) =>
    onChange(attendees.filter((a) => a.optionId !== optionId));
  const setFunktion = (optionId, funktion) =>
    onChange(
      attendees.map((a) => (a.optionId === optionId ? { ...a, funktion } : a))
    );

  return (
    <div className="sitzungen__attendee-select">
      {attendees.length > 0 && (
        <ul className="sitzungen__attendee-list">
          {attendees.map((a) => (
            <li key={a.optionId} className="sitzungen__attendee-item">
              <span className="sitzungen__attendee-name">{a.label}</span>
              <input
                type="text"
                placeholder="Funktion (optional)"
                value={a.funktion}
                onChange={(e) => setFunktion(a.optionId, e.target.value)}
              />
              <button
                type="button"
                onClick={() => remove(a.optionId)}
                aria-label={`${a.label} entfernen`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="sitzungen__attendee-input-wrap">
        <input
          type="text"
          value={query}
          placeholder="Anwesende suchen / hinzufügen…"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && (available.length > 0 || canAddFree) && (
          <ul className="sitzungen__attendee-dropdown">
            {available.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(o);
                  }}
                >
                  {o.label}
                </button>
              </li>
            ))}
            {canAddFree && (
              <li>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addFree(q);
                  }}
                >
                  ＋ „{q}" hinzufügen
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

const SitzungFields = ({ form, onChange, options }) => (
  <div className="sitzungen__form-grid">
    <label className="sitzungen__field">
      <span>Art</span>
      <input
        type="text"
        list="sitzungsarten"
        value={form.art}
        onChange={(e) => onChange("art", e.target.value)}
      />
      <datalist id="sitzungsarten">
        {SITZUNG_ARTEN.map((a) => (
          <option key={a} value={a} />
        ))}
      </datalist>
    </label>
    <label className="sitzungen__field">
      <span>Nummer</span>
      <input type="text" value={form.nr} onChange={(e) => onChange("nr", e.target.value)} />
    </label>
    <label className="sitzungen__field">
      <span>Datum</span>
      <input
        type="date"
        value={form.datum}
        onChange={(e) => onChange("datum", e.target.value)}
      />
    </label>
    <label className="sitzungen__field">
      <span>Ort</span>
      <input
        type="text"
        value={form.ort}
        onChange={(e) => onChange("ort", e.target.value)}
        placeholder="optional"
      />
    </label>
    <div className="sitzungen__field sitzungen__field--full">
      <span>Anwesende</span>
      <AttendeeSelect
        options={options}
        attendees={form.anwesende}
        onChange={(anwesende) => onChange("anwesende", anwesende)}
      />
    </div>
  </div>
);

// --- Protokoll generieren/versenden (US-SITZ-002, PDF seit US-SITZ-005) ------
// Exportiert, damit der Sitzungsmodus in Pendenzen.jsx dasselbe Modal für die
// laufende Sitzung öffnen kann (statt Excel-/PDF-Export zu duplizieren).
export const ProtokollModal = ({ sitzung, project, pendenzen, sitzungen, sources, options, onClose }) => {
  // EIN gemeinsames Dropdown fuer Excel UND PDF (zwei getrennte Dropdowns
  // mit denselben zwei Optionen wirkten redundant/verwirrend, Nutzer-
  // Rueckmeldung "zweimal intern/extern"). Default "extern": beide Formate
  // werden eher weitergegeben/geteilt als dass sie nur intern bleiben,
  // daher ist die sicherere Voreinstellung fuer beide dieselbe.
  const [modus, setModus] = useState("extern");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Ersetzt einen frueheren einzelnen "done"-Boolean: mit zwei Formaten
  // (Excel/PDF) muss der Hinweistext den zuletzt heruntergeladenen Dateinamen
  // nennen, nicht pauschal "das Protokoll".
  const [downloadedFile, setDownloadedFile] = useState(null);

  // PDF-spezifische Optionen (US-SITZ-005) — bewusst nur lokaler Modal-State,
  // nicht auf der Sitzung persistiert (vermeidet eine Schemaaenderung und das
  // bekannte Risiko divergierender Werte zwischen zwei Formularen, vgl.
  // vonZeit/bisZeit zwischen diesem Tab und dem Sitzungsmodus-Panel).
  const [showNotizen, setShowNotizen] = useState(true);
  const [showFolien, setShowFolien] = useState(true);
  // Nummer als eigene Spalte vor der Folien-/Text-Spalte — bewusst per
  // Default aus (nur bei Bedarf aktivierbar), siehe ADR 0062 Nachtrag.
  const [showNummer, setShowNummer] = useState(false);
  const [hatEntschuldigte, setHatEntschuldigte] = useState(false);
  const [entschuldigte, setEntschuldigte] = useState([]);
  const [andererVerteiler, setAndererVerteiler] = useState(false);
  const [verteilerText, setVerteilerText] = useState("");
  // Anhang nur als Freitext-Hinweis (kein Datei-Upload) — z.B. "siehe Anhang"
  // im Protokolltext verweist oft auf eine separat zirkulierte Beilage.
  const [hatAnhang, setHatAnhang] = useState(false);
  const [anhangText, setAnhangText] = useState("");

  const pendenzenDieserSitzung = pendenzen.filter((p) => p.sitzungId === sitzung.id);
  // "Offene Pendenzen" (Typ P, nicht erledigt) aus zeitlich früheren Sitzungen des
  // Projekts – als Übertrag ins Protokoll (nur Excel, siehe ADR 0062).
  const offenePendenzenFrueher = pendenzen.filter((p) => {
    if (p.typ !== "P" || p.status === "erledigt") return false;
    if (!p.sitzungId || p.sitzungId === sitzung.id) return false;
    const andere = sitzungen.find((s) => s.id === p.sitzungId);
    return andere && sitzung.datum && andere.datum && andere.datum < sitzung.datum;
  });

  const recipients = buildProtokollRecipients(sitzung, sources);
  const dateiname = protokollFilename(project, sitzung);

  const handleDownload = async () => {
    try {
      setBusy(true);
      setError(null);
      await downloadProtokoll({
        sitzung,
        project,
        pendenzenDieserSitzung,
        offenePendenzenFrueher,
        modus,
      });
      setDownloadedFile({ name: dateiname, kind: "excel" });
    } catch (err) {
      setError(err.message || "Das Protokoll konnte nicht erzeugt werden.");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setBusy(true);
      setError(null);
      const { folien } = showFolien ? await getPraesentation(sitzung.id) : { folien: [] };
      await downloadSitzungPdf({
        sitzung,
        project,
        pendenzenDieserSitzung,
        modus,
        showNotizen,
        showFolien,
        showNummer,
        entschuldigte: hatEntschuldigte ? entschuldigte : [],
        verteilerText: andererVerteiler ? verteilerText : "",
        anhangText: hatAnhang ? anhangText : "",
        folien,
      });
      setDownloadedFile({ name: sitzungPdfFilename(project, sitzung), kind: "pdf" });
    } catch (err) {
      setError(err.message || "Das PDF konnte nicht erzeugt werden.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrepareMail = () => {
    const mailto = buildProtokollMailto({ recipients, project, sitzung, dateiname });
    window.location.href = mailto;
  };

  return (
    <div className="sitzungen__modal-overlay" onClick={onClose}>
      <div className="sitzungen__modal" onClick={(e) => e.stopPropagation()}>
        <div className="sitzungen__modal-header">
          <h3>Protokoll: {sitzung.art} Nr. {sitzung.nr}</h3>
          <button type="button" className="sitzungen__modal-close" onClick={onClose} aria-label="Schliessen">
            ×
          </button>
        </div>
        <div className="sitzungen__modal-body">
          {error && (
            <div className="sitzungen__error" role="alert">
              {error}
            </div>
          )}
          <p>
            {pendenzenDieserSitzung.length} Eintrag(e) dieser Sitzung,{" "}
            {offenePendenzenFrueher.length} offene Pendenz(en) als Übertrag.
          </p>
          <label className="sitzungen__field">
            <span>Protokoll-Art</span>
            <select value={modus} onChange={(e) => setModus(e.target.value)}>
              <option value="extern">Extern (nur „Malheur&Fortuna", nur „extern sichtbar")</option>
              <option value="intern">Intern (volle Namen, alle Einträge)</option>
            </select>
          </label>
          <p className="sitzungen__hint">
            Gilt für Excel und PDF gleichermassen. Beide Formate werden eher
            weitergegeben als dass sie nur intern bleiben — deshalb
            standardmässig ohne persönliche Namen; bei Bedarf hier auf
            „Intern" umstellen.
          </p>
          <p className="sitzungen__hint">
            {recipients.length > 0
              ? `E-Mail-Vorbereitung mit ${recipients.length} Empfänger:in(nen) aus den Anwesenden.`
              : "Keine E-Mail-Adressen der Anwesenden gefunden – Empfängerliste bleibt leer."}
          </p>

          <div className="sitzungen__pdf-options">
            <h4>PDF-Optionen</h4>
            <label className="sitzungen__checkbox-row">
              <input
                type="checkbox"
                checked={showNotizen}
                onChange={(e) => setShowNotizen(e.target.checked)}
              />
              <span>Notizen anzeigen</span>
            </label>
            <label className="sitzungen__checkbox-row">
              <input
                type="checkbox"
                checked={showFolien}
                onChange={(e) => setShowFolien(e.target.checked)}
              />
              <span>Folien / angehängte Dokumente anzeigen</span>
            </label>
            <label className="sitzungen__checkbox-row">
              <input
                type="checkbox"
                checked={showNummer}
                onChange={(e) => setShowNummer(e.target.checked)}
              />
              <span>Nummer als eigene Spalte anzeigen</span>
            </label>
            <label className="sitzungen__checkbox-row">
              <input
                type="checkbox"
                checked={hatEntschuldigte}
                onChange={(e) => setHatEntschuldigte(e.target.checked)}
              />
              <span>Es gab entschuldigte Personen</span>
            </label>
            {hatEntschuldigte && (
              <div className="sitzungen__field sitzungen__field--full">
                <span>Entschuldigt</span>
                <AttendeeSelect
                  options={options}
                  attendees={entschuldigte}
                  onChange={setEntschuldigte}
                />
              </div>
            )}
            <label className="sitzungen__checkbox-row">
              <input
                type="checkbox"
                checked={andererVerteiler}
                onChange={(e) => setAndererVerteiler(e.target.checked)}
              />
              <span>Anderer Verteiler als die Anwesenden</span>
            </label>
            {andererVerteiler && (
              <label className="sitzungen__field sitzungen__field--full">
                <span>Verteiler</span>
                <textarea
                  rows={2}
                  value={verteilerText}
                  onChange={(e) => setVerteilerText(e.target.value)}
                  placeholder="z.B. Bauherrschaft, Planungsteam …"
                />
              </label>
            )}
            <label className="sitzungen__checkbox-row">
              <input
                type="checkbox"
                checked={hatAnhang}
                onChange={(e) => setHatAnhang(e.target.checked)}
              />
              <span>Anhang erfassen</span>
            </label>
            {hatAnhang && (
              <label className="sitzungen__field sitzungen__field--full">
                <span>Anhang</span>
                <textarea
                  rows={2}
                  value={anhangText}
                  onChange={(e) => setAnhangText(e.target.value)}
                  placeholder="z.B. Plan Erdgeschoss V2, Kostenschätzung.xlsx (nur als Hinweistext, keine Datei wird angehängt)"
                />
              </label>
            )}
          </div>

          {downloadedFile && (
            <p className="sitzungen__hint">
              Datei „{downloadedFile.name}" wurde heruntergeladen.
              {downloadedFile.kind === "excel" && " Bitte im E-Mail-Programm manuell anhängen."}
            </p>
          )}
        </div>
        <div className="sitzungen__modal-footer">
          <button type="button" className="sitzungen__btn" onClick={onClose}>
            Schliessen
          </button>
          <button
            type="button"
            className="sitzungen__btn"
            onClick={handlePrepareMail}
            disabled={!downloadedFile}
          >
            E-Mail vorbereiten
          </button>
          <button type="button" className="sitzungen__btn" onClick={handleDownload} disabled={busy}>
            {busy ? "Erzeuge..." : "Als Excel herunterladen"}
          </button>
          <button
            type="button"
            className="sitzungen__btn sitzungen__btn--primary"
            onClick={handleDownloadPdf}
            disabled={busy}
          >
            {busy ? "Erzeuge..." : "Als PDF herunterladen"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Sitzungen = ({ project, readOnly = false }) => {
  const { user } = useAuth();
  const projectUuid = project?.id;
  const projectCode = project?.code;

  const [sitzungen, setSitzungen] = useState([]);
  const [partners, setPartners] = useState([]);
  const [persons, setPersons] = useState([]);
  const [relations, setRelations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendenzen, setPendenzen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [protokollSitzung, setProtokollSitzung] = useState(null);

  const options = useMemo(
    () => buildResponsibleOptions(partners, persons, relations, teamMembers),
    [partners, persons, relations, teamMembers]
  );

  const loadData = useCallback(async () => {
    if (!projectUuid) return;
    try {
      setLoading(true);
      setError(null);
      const [sitzungenData, partnersData, personsData, relationsData, teamData, pendenzenData] =
        await Promise.all([
          getSitzungen(projectUuid),
          getAllPartners(),
          getAllPersons(),
          getProjectRelations(projectCode),
          getAllTeamMembers(),
          getPendenzen(projectUuid),
        ]);
      setSitzungen(sitzungenData);
      setPartners(partnersData);
      setPersons(personsData);
      setRelations(relationsData);
      setTeamMembers(teamData);
      setPendenzen(pendenzenData);
    } catch (err) {
      setError(err.message || "Die Sitzungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [projectUuid, projectCode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    const art = SITZUNG_ARTEN[0];
    setForm({ ...emptyForm(), art, nr: suggestNextNr(sitzungen, art) });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setForm({ art: s.art, nr: s.nr, datum: s.datum, ort: s.ort, anwesende: s.anwesende });
    setEditing(s);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "art" && !editing) {
        next.nr = suggestNextNr(sitzungen, value);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      if (editing) {
        await updateSitzung(projectUuid, editing.id, form);
      } else {
        await createSitzung(projectUuid, form, user?.email || user?.userDetails || "");
      }
      closeForm();
      await loadData();
    } catch (err) {
      setError(err.message || "Die Sitzung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (
      !window.confirm(
        `Sitzung „${s.art} Nr. ${s.nr}" wirklich löschen? Zugeordnete Pendenzen bleiben erhalten, verlieren aber die Sitzungszuordnung. Eine ggf. hochgeladene Präsentation wird mitgelöscht.`
      )
    ) {
      return;
    }
    try {
      setError(null);
      await deleteSitzung(projectUuid, s.id);
      await loadData();
    } catch (err) {
      setError(err.message || "Die Sitzung konnte nicht gelöscht werden.");
    }
  };

  if (loading) {
    return <div className="sitzungen sitzungen--loading">Sitzungen werden geladen...</div>;
  }

  return (
    <div className="sitzungen">
      <div className="sitzungen__header">
        <h3>Sitzungen</h3>
        {!readOnly && (
          <button type="button" className="sitzungen__btn sitzungen__btn--primary" onClick={openCreate}>
            + Neue Sitzung
          </button>
        )}
      </div>

      {error && (
        <div className="sitzungen__error" role="alert" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      <div className="sitzungen__table-wrapper">
        <table className="sitzungen__table">
          <thead>
            <tr>
              <th>Art</th>
              <th>Nr</th>
              <th>Datum</th>
              <th>Ort</th>
              <th>Anwesende</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sitzungen.length === 0 ? (
              <tr>
                <td className="sitzungen__empty" colSpan={6}>
                  Noch keine Sitzungen erfasst.
                </td>
              </tr>
            ) : (
              sitzungen.map((s) => (
                <tr
                  key={s.id}
                  className="sitzungen__row"
                  onClick={() => (readOnly ? null : openEdit(s))}
                  title={readOnly ? undefined : "Zum Bearbeiten anklicken"}
                >
                  <td>{s.art}</td>
                  <td>{s.nr}</td>
                  <td>{formatDate(s.datum)}</td>
                  <td>{s.ort}</td>
                  <td>{(s.anwesende || []).map((a) => a.label).join(", ")}</td>
                  <td className="sitzungen__row-actions">
                    <button
                      type="button"
                      className="sitzungen__btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProtokollSitzung(s);
                      }}
                    >
                      Protokoll
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        className="sitzungen__btn sitzungen__btn--danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(s);
                        }}
                      >
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="sitzungen__modal-overlay" onClick={closeForm}>
          <div className="sitzungen__modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit}>
              <div className="sitzungen__modal-header">
                <h3>{editing ? "Sitzung bearbeiten" : "Neue Sitzung"}</h3>
                <button
                  type="button"
                  className="sitzungen__modal-close"
                  onClick={closeForm}
                  aria-label="Schliessen"
                >
                  ×
                </button>
              </div>
              <div className="sitzungen__modal-body">
                <SitzungFields form={form} onChange={handleChange} options={options} />
              </div>
              <div className="sitzungen__modal-footer">
                <button type="button" className="sitzungen__btn" onClick={closeForm} disabled={saving}>
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="sitzungen__btn sitzungen__btn--primary"
                  disabled={saving}
                >
                  {saving ? "Speichern..." : "Speichern"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {protokollSitzung && (
        <ProtokollModal
          sitzung={protokollSitzung}
          project={project}
          pendenzen={pendenzen}
          sitzungen={sitzungen}
          sources={{ partners, persons, teamMembers }}
          options={options}
          onClose={() => setProtokollSitzung(null)}
        />
      )}
    </div>
  );
};

export default Sitzungen;
